import { call, put, takeEvery } from 'redux-saga/effects'
import type { AsyncAction } from 'src/utils/reduxUtils'
import {
  AdminFetchDocusignPartnerTemplates,
  AdminFetchLmsTrainingList,
  AdminFetchYardstikPackages,
} from 'src/constants/actionTypes'
import { callSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import type { DocusignPartnerContractTemplate } from 'src/interfaces/collectStepReference'
import type { LmsTrainingPlain } from 'src/interfaces/collectStepReference'
import type { YardstikPackage } from 'src/interfaces/collectStepReference'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'

interface FetchDocusignPayload {
  partnerId: string
}

function* onFetchDocusignPartnerTemplates(action: AsyncAction<FetchDocusignPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminDocusignPartnerTemplates,
      {
        method: 'GET',
        query: { partnerId },
      },
    )

    const list = normalizeApiListPayload<DocusignPartnerContractTemplate>(payload)

    yield put(AdminFetchDocusignPartnerTemplates.success(list))

    if (action.next) {
      action.next(null, list)
    }
  } catch (err: unknown) {
    const message =
      typeof (err as { payload?: { message?: string } })?.payload?.message === 'string'
        ? (err as { payload: { message: string } }).payload.message
        : 'Failed to fetch DocuSign templates'

    yield put(AdminFetchDocusignPartnerTemplates.failed(err))

    if (action.next) {
      action.next(message)
    }
  }
}

function* onFetchLmsTrainingList(action: AsyncAction<void>) {
  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminLmsTrainingList,
      {
        method: 'GET',
      },
    )

    const list = normalizeApiListPayload<LmsTrainingPlain>(payload)

    yield put(AdminFetchLmsTrainingList.success(list))

    if (action.next) {
      action.next(null, list)
    }
  } catch (err: unknown) {
    const message =
      typeof (err as { payload?: { message?: string } })?.payload?.message === 'string'
        ? (err as { payload: { message: string } }).payload.message
        : 'Failed to fetch LMS trainings'

    yield put(AdminFetchLmsTrainingList.failed(err))

    if (action.next) {
      action.next(message)
    }
  }
}

interface FetchYardstikPayload {
  partnerId: string
}

function* onFetchYardstikPackages(action: AsyncAction<FetchYardstikPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminYardstikPackages,
      {
        method: 'GET',
        query: { partnerId },
      },
    )

    const list = normalizeApiListPayload<YardstikPackage>(payload)

    yield put(AdminFetchYardstikPackages.success(list))

    if (action.next) {
      action.next(null, list)
    }
  } catch (err: unknown) {
    const message =
      typeof (err as { payload?: { message?: string } })?.payload?.message === 'string'
        ? (err as { payload: { message: string } }).payload.message
        : 'Failed to fetch Yardstik packages'

    yield put(AdminFetchYardstikPackages.failed(err))

    if (action.next) {
      action.next(message)
    }
  }
}

export function* collectStepReferenceSaga() {
  yield takeEvery(AdminFetchDocusignPartnerTemplates.type.REQUEST, onFetchDocusignPartnerTemplates)
  yield takeEvery(AdminFetchLmsTrainingList.type.REQUEST, onFetchLmsTrainingList)
  yield takeEvery(AdminFetchYardstikPackages.type.REQUEST, onFetchYardstikPackages)
}
